import React from "react";
import Icon from "../shared/Icon";
import StatusPill from "../shared/StatusPill";
import PaymentPill from "../shared/PaymentPill";
import WoshId from "../shared/WoshId";
import { EmptyState, LoadingRows } from "../shared/States";
import { formatCurrency, formatKilos, formatShortDate } from "../shared/format";

/**
 * Une commande = une rangée, tout sur la même ligne.
 *
 * Le statut occupe sa propre colonne plutôt qu'une ligne de rappel sous chaque
 * commande : on lit deux fois plus de commandes à l'écran, et l'œil peut
 * balayer la colonne de statut verticalement au lieu de sauter d'une ligne
 * à l'autre.
 */
const GRID = "44px 1.05fr 1.5fr .8fr 1.05fr 1.1fr 1.1fr .5fr .6fr .85fr 60px";

const SortHeader = ({ label, sortKey, sort, onSort }) => (
  <button type="button" className="mgr-sortable" onClick={() => onSort(sortKey)}>
    {label}
    {sort.key === sortKey ? (sort.direction === "asc" ? " ▲" : " ▼") : " ▾"}
  </button>
);

const Check = ({ checked, onChange, label }) => (
  <button
    type="button"
    className={`mgr-check${checked ? " is-on" : ""}`}
    onClick={(event) => {
      event.stopPropagation();
      onChange();
    }}
    aria-pressed={checked}
    aria-label={label}
  >
    <Icon name="checkmark-sharp" style={{ fontSize: 13 }} />
  </button>
);

const OrdersTable = ({
  orders,
  isLoading,
  selectedIds,
  onToggle,
  onToggleAll,
  onOpen,
  onDownloadReceipt,
  sort,
  onSort,
  totals,
  metricsFor,
  emptyTitle,
  emptyHint,
  t,
}) => {
  const allSelected = orders.length > 0 && orders.every((order) => selectedIds.includes(order.id));

  return (
    <div className="mgr-table">
      <div className="mgr-thead" style={{ gridTemplateColumns: GRID }}>
        <div>
          <Check checked={allSelected} onChange={onToggleAll} label={t("Alles selecteren")} />
        </div>
        <div>{t("Nr.")}</div>
        <div>
          <SortHeader label={t("Bewoner")} sortKey="residentName" sort={sort} onSort={onSort} />
        </div>
        <div>
          <SortHeader label={t("Opgehaald")} sortKey="pickupDate" sort={sort} onSort={onSort} />
        </div>
        <div>{t("Terug verwacht")}</div>
        <div>{t("Status")}</div>
        <div>{t("Betaling")}</div>
        <div className="mgr-num">{t("Kg")}</div>
        <div className="mgr-num">{t("Zakken")}</div>
        <div className="mgr-num">
          <SortHeader label={t("Prijs")} sortKey="total" sort={sort} onSort={onSort} />
        </div>
        <div className="mgr-num">{t("Bon")}</div>
      </div>

      {isLoading ? <LoadingRows rows={6} /> : null}

      {!isLoading && orders.length === 0 ? (
        <EmptyState title={emptyTitle} hint={emptyHint} />
      ) : null}

      {!isLoading &&
        orders.map((order) => {
          const selected = selectedIds.includes(order.id);
          const metrics = metricsFor ? metricsFor(order) : order;
          return (
            <div
              key={order.id}
              role="button"
              tabIndex={0}
              className={`mgr-tr${selected ? " is-selected" : ""}`}
              style={{
                gridTemplateColumns: GRID,
                borderBottom: "1px solid var(--mgr-hairline)",
              }}
              onClick={() => onOpen(order)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onOpen(order);
                }
              }}
            >
              <div>
                <Check
                  checked={selected}
                  onChange={() => onToggle(order.id)}
                  label={`${t("Selecteren")} ${order.reference}`}
                />
              </div>
              <div>
                <WoshId value={order.reference} size="sm" t={t} />
              </div>
              <div style={{ fontWeight: 700 }}>
                {order.residentName || "—"}
                {order.room ? (
                  <span style={{ fontWeight: 600, color: "var(--mgr-ink-3)" }}>
                    {" "}
                    · {t("k.")} {order.room}
                  </span>
                ) : null}
              </div>
              <div style={{ fontWeight: 600 }}>{formatShortDate(order.pickupDate)}</div>
              <div className={order.isLate ? "mgr-late" : undefined} style={{ fontWeight: 600 }}>
                {formatShortDate(order.deliveryDate)}
                {order.isLate ? ` · ${t("te laat")}` : ""}
              </div>
              <div>
                <StatusPill state={order.state} t={t} />
              </div>
              <div>
                <PaymentPill status={order.paymentStatus} t={t} />
              </div>
              <div className="mgr-num" style={{ fontWeight: 700 }}>
                {metrics.kilos ? formatKilos(metrics.kilos) : "—"}
              </div>
              <div className="mgr-num" style={{ fontWeight: 600 }}>
                {metrics.bags ? metrics.bags : "—"}
              </div>
              <div className="mgr-num" style={{ fontWeight: 800 }}>
                {formatCurrency(order.total)}
              </div>
              <div className="mgr-num" style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="mgr-link"
                  aria-label={`${t("Bon downloaden")} ${order.reference}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onDownloadReceipt(order);
                  }}
                >
                  <Icon name="download-outline" style={{ fontSize: 19 }} />
                </button>
              </div>
            </div>
          );
        })}

      {!isLoading && orders.length > 0 ? (
        <div className="mgr-tfoot">
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--mgr-ink-2)" }}>
            {t("Totaal van deze selectie van filters")} — {totals.count} {t("bestellingen")}
            {totals.kilos > 0 ? `, ${formatKilos(totals.kilos)} kg` : ""}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default OrdersTable;
